import {
  LanguageItem,
  PreTranslateInfo,
  startTranslate,
  TranslateOptions,
  type TranslationSession,
} from '@/app/services/translation';
import {
  getTranslateDetailQueryKey,
  translateHistoryInfiniteKey,
} from '@/components/histories/translate-history-polling';
import {
  insertSidebarTranslationHistory,
  sidebarHistoryKeys,
} from '@/components/sidebar-history/sidebar-history-cache';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePointInfo } from '@/hooks/use-point-info';
import { useAuth } from '@/providers/auth-provider';
import { sendGTMEvent } from '@next/third-parties/google';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { AlertTriangleIcon, ArrowRightIcon, Loader2Icon } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useState, useTransition } from 'react';
import { toast } from 'sonner';

function allowRadixPopoverInteraction(target: EventTarget | null) {
  if (
    target instanceof HTMLElement &&
    target.closest('[data-radix-popper-content-wrapper]')
  ) {
    return true;
  }
  return false;
}

function buildOptimisticTranslationSession(params: {
  uid?: string;
  now: string;
  title: string;
  sourceFileName: string;
  sourceFileId: string;
  sourceLang: string;
  targetLang: string;
  sourceNImages: number;
  sourceNImagesToTranslate: number;
  sourceNChars: number;
  sourceNTokens: number;
  translatePoint: number;
  optimizeMathFormula: boolean;
  translateImage: boolean;
  pageSource: string;
  sessionId: string;
}): TranslationSession {
  return {
    error_msg: '',
    uid: params.uid ?? '',
    title: params.title,
    status: 'INIT',
    ctime: params.now,
    mtime: params.now,
    file_translation_session_id: params.sessionId,
    source_file_id: params.sourceFileId,
    source_file_access_url: '',
    source_preview_file_url: '',
    source_n_images: params.sourceNImages,
    source_n_images_to_translate: params.sourceNImagesToTranslate,
    source_file_name: params.sourceFileName,
    source_file_mime_type: '',
    source_file_size: 0,
    target_file_mime_type: '',
    source_n_chars: params.sourceNChars,
    source_n_tokens: params.sourceNTokens,
    source_lang: params.sourceLang,
    translate_point: params.translatePoint,
    target_file_id: '',
    target_file_access_url: '',
    target_preview_file_url: '',
    target_lang: params.targetLang,
    target_file_name: '',
    target_file_size: 0,
    optimize_math_formula: params.optimizeMathFormula,
    translate_image: params.translateImage,
    task_name: '',
    task_progress: '',
    global_progress_percent: 0,
    page_source: params.pageSource,
  };
}

function insertDefaultTranslateHistory(
  prev: InfiniteData<PageInfo<TranslationSession>> | undefined,
  item: TranslationSession,
) {
  if (!prev?.pages.length) {
    return prev;
  }

  const firstPage = prev.pages[0];
  const nextFirstList = [
    item,
    ...firstPage.data.list.filter(
      (current) =>
        current.file_translation_session_id !==
        item.file_translation_session_id,
    ),
  ].slice(0, firstPage.data.list.length);

  return {
    ...prev,
    pages: prev.pages.map((page, index) =>
      index === 0
        ? {
            ...page,
            data: {
              ...page.data,
              list: nextFirstList,
              total: page.data.total + 1,
            },
          }
        : page,
    ),
  };
}

function getAnySourceLanguage(options: LanguageItem[]) {
  return (
    options.find(
      (item) => item.value === 'auto' || item.name.includes('任意'),
    ) ?? options[0]
  );
}

function resolveSourceLanguage(
  detectedSourceLang: string,
  languageOptions: LanguageItem[],
) {
  if (languageOptions.some((item) => item.value === detectedSourceLang)) {
    return detectedSourceLang;
  }
  return getAnySourceLanguage(languageOptions)?.value ?? detectedSourceLang;
}

const TranslationFileOptionsModal = ({
  isOpen,
  onOpenChange,
  fileInfo,
  pageSource,
  languageOptions,
}: {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  fileInfo: PreTranslateInfo | null;
  pageSource: string;
  languageOptions: LanguageItem[];
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<TranslateOptions>({
    source_file_id: '',
    source_lang: 'zh-cn',
    target_lang: 'en',
    optimize_math_formula: false,
    translate_image: false,
    page_source: pageSource,
  });
  const { info, getInfo } = usePointInfo();
  const { user } = useAuth();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [isPending, startTransition] = useTransition();

  const defaultTargetLang = useMemo(() => {
    if (!fileInfo) return 'zh-cn';
    if (fileInfo.detected_source_lang === 'en') return 'zh-cn';
    if (
      fileInfo.detected_source_lang === 'zh-cn' ||
      fileInfo.detected_source_lang === 'zh-tw'
    ) {
      return 'en';
    }
    return 'zh-cn';
  }, [fileInfo]);

  const defaultSourceLang = useMemo(() => {
    if (!fileInfo) return 'auto';
    return resolveSourceLanguage(
      fileInfo.detected_source_lang,
      languageOptions,
    );
  }, [fileInfo, languageOptions]);

  const effectiveOptions = useMemo(() => {
    if (!fileInfo) return options;

    const isSameFile = options.source_file_id === fileInfo.source_file_id;
    const sourceLang = isSameFile ? options.source_lang : defaultSourceLang;
    return {
      ...options,
      source_file_id: fileInfo.source_file_id,
      source_lang: resolveSourceLanguage(sourceLang, languageOptions),
      target_lang: isSameFile ? options.target_lang : defaultTargetLang,
      page_source: pageSource,
    };
  }, [
    defaultSourceLang,
    defaultTargetLang,
    fileInfo,
    languageOptions,
    options,
    pageSource,
  ]);

  const pointInfo = useMemo(() => {
    if (!fileInfo) return info;
    return {
      ...info,
      available: fileInfo.user_available_point,
    };
  }, [fileInfo, info]);

  const calculatePoint = (): number => {
    const translatePointInfo = fileInfo?.translate_point_info;
    const {
      total_token_point = 0,
      total_image_point = 0,
      optimize_math_formula_point = 0,
    } = translatePointInfo || {};
    return (
      (fileInfo?.ext === '.pdf' && options.optimize_math_formula
        ? optimize_math_formula_point
        : 0) +
      (options.translate_image
        ? total_image_point + total_token_point
        : total_token_point)
    );
  };

  async function onStartTranslate() {
    if (!options.source_lang) {
      toast.warning('请选择源语言', {
        position: 'top-center',
      });
      return;
    }
    if (!options.target_lang) {
      toast.warning('请选择目标语言', {
        position: 'top-center',
      });
      return;
    }
    setIsLoading(true);
    try {
      const { data } = await startTranslate(effectiveOptions);
      const now = new Date().toISOString();
      const optimisticItem = buildOptimisticTranslationSession({
        uid: user?.uid,
        now,
        title: data.title,
        sourceFileName: data.source_file_name,
        sourceFileId: effectiveOptions.source_file_id,
        sourceLang: data.source_lang,
        targetLang: data.target_lang,
        sourceNImages: data.source_n_images,
        sourceNImagesToTranslate: data.source_n_images_to_translate,
        sourceNChars: data.source_n_chars,
        sourceNTokens: data.source_n_tokens,
        translatePoint: data.translate_point,
        optimizeMathFormula: data.optimize_math_formula,
        translateImage: data.translate_image,
        pageSource: effectiveOptions.page_source,
        sessionId: data.file_translation_session_id,
      });

      insertSidebarTranslationHistory(queryClient, optimisticItem);
      queryClient.setQueryData(
        getTranslateDetailQueryKey(data.file_translation_session_id),
        optimisticItem,
      );
      queryClient.setQueryData(
        [...translateHistoryInfiniteKey, ''],
        (prev: InfiniteData<PageInfo<TranslationSession>> | undefined) =>
          insertDefaultTranslateHistory(prev, optimisticItem),
      );
      void queryClient.invalidateQueries({
        queryKey: sidebarHistoryKeys.translate,
      });
      void queryClient.invalidateQueries({
        queryKey: translateHistoryInfiniteKey,
      });
      sendGTMEvent({
        event: 'generate_translation',
        value: data.source_file_name,
      });
      startTransition(() => {
        router.push(
          `/translate/preview?id=${data.file_translation_session_id}`,
        );
      });
    } catch (e) {
      console.error(e);
    }
    setIsLoading(false);
  }

  const showBuyBtn = useMemo(() => {
    const point = calculatePoint();
    if (pointInfo.task_count_deep_research > 0) {
      return (
        pointInfo.available <
        pointInfo.total_running_task_estimated_point_deep_research + point
      );
    } else {
      return pointInfo.available < point;
    }
  }, [options.optimize_math_formula, options.translate_image, pointInfo]);

  const disableOptimizeMathFormula = useMemo(() => {
    return (fileInfo?.page_num ?? 0) > 50;
  }, [fileInfo]);

  const targetLanguageOptions = useMemo(() => {
    let options = [...languageOptions];

    // 将中文和英文项移到列表前面，中文在英文前面
    const enItemIndex = options.findIndex((item) => item.value === 'en');
    const zhCnItemIndex = options.findIndex((item) => item.value === 'zh-cn');

    if (enItemIndex !== -1 && zhCnItemIndex !== -1) {
      const enItem = options[enItemIndex];
      const zhCnItem = options[zhCnItemIndex];

      // 移除这两个项
      options = options.filter(
        (item) =>
          item.value !== 'en' &&
          item.value !== 'zh-cn' &&
          item.value !== 'auto',
      );

      // 将中文和英文项添加到列表开头
      options.unshift(enItem);
      options.unshift(zhCnItem);
    }

    return options;
  }, [languageOptions]);

  useEffect(() => {
    if (isOpen && fileInfo) {
      getInfo();
    }
  }, [fileInfo, getInfo, isOpen]);

  // Reset language selection when the file changes. Render-time state sync
  // per React docs:
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [lastSyncedFileId, setLastSyncedFileId] = useState<string | null>(null);
  if (fileInfo && lastSyncedFileId !== fileInfo.source_file_id) {
    setLastSyncedFileId(fileInfo.source_file_id);
    setOptions((prev) => ({
      ...prev,
      source_file_id: fileInfo.source_file_id,
      source_lang: defaultSourceLang,
      target_lang: defaultTargetLang,
    }));
  }

  if (!fileInfo) return null;

  const requiredPoint = calculatePoint();
  const retentionDays = user?.membership ? 365 : 30;
  const hasRunningTaskPointRisk =
    pointInfo.task_count_deep_research > 0 &&
    pointInfo.available <
      pointInfo.total_running_task_estimated_point_deep_research +
        requiredPoint;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[min(92vw,560px)] overflow-hidden rounded-2xl border-border/50 bg-card p-0 sm:max-w-140 [&>button]:right-5 [&>button]:top-5"
        onInteractOutside={(e) => {
          if (allowRadixPopoverInteraction(e.target)) {
            return;
          }
          e.preventDefault();
        }}
      >
        <DialogHeader className="px-6 pb-2 pt-6 pr-12 sm:px-8 sm:pt-7">
          <DialogTitle className="text-xl font-semibold tracking-tight">
            确认翻译设置
          </DialogTitle>
          <div className="mt-2 line-clamp-2 break-all text-sm leading-6 text-muted-foreground">
            {fileInfo.source_file_name}
          </div>
        </DialogHeader>

        <div className="space-y-4 px-6 py-4 sm:px-8">
          <section className="space-y-2">
            <div className="text-sm font-semibold">语言</div>
            <div className="grid grid-cols-[1fr_32px_1fr] items-center rounded-xl bg-muted/75 p-1.5">
              <Select
                value={effectiveOptions.source_lang}
                onValueChange={(value) => {
                  if (!value) return;
                  setOptions((prevState) => ({
                    ...prevState,
                    source_lang: value,
                    target_lang:
                      value === effectiveOptions.target_lang
                        ? ''
                        : effectiveOptions.target_lang,
                  }));
                }}
              >
                <SelectTrigger className="h-10 w-full rounded-xl border-0 bg-transparent shadow-none focus:ring-0">
                  <SelectValue placeholder="请选择" />
                </SelectTrigger>
                <SelectContent className="rounded-xl p-1.5">
                  {languageOptions.map((item) => (
                    <SelectItem
                      key={item.value}
                      value={item.value}
                      className="rounded-lg"
                    >
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-background/80 text-primary shadow-sm">
                <ArrowRightIcon className="h-4 w-4" />
              </div>
              <Select
                value={effectiveOptions.target_lang}
                onValueChange={(value) => {
                  if (!value) return;
                  setOptions((prevState) => ({
                    ...prevState,
                    target_lang: value,
                  }));
                }}
              >
                <SelectTrigger className="h-10 w-full rounded-xl border-0 bg-transparent shadow-none focus:ring-0">
                  <SelectValue placeholder="请选择" />
                </SelectTrigger>
                <SelectContent className="rounded-xl p-1.5">
                  {targetLanguageOptions.map((item) => (
                    <SelectItem
                      key={item.value}
                      value={item.value}
                      disabled={item.value === effectiveOptions.source_lang}
                      className="rounded-lg"
                    >
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </section>

          {fileInfo?.ext === '.pdf' && (
            <section className="flex flex-col gap-3 rounded-xl bg-muted/30 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-sm font-semibold">公式优化</div>
                <div className="mt-0.5 text-xs leading-5 text-muted-foreground">
                  {disableOptimizeMathFormula
                    ? '超过 50 页，已禁用。'
                    : '公式更多时建议开启，每页额外 10 积分。'}
                </div>
              </div>
              <RadioGroup
                orientation="horizontal"
                value={String(options.optimize_math_formula)}
                onValueChange={(value) => {
                  setOptions((prevState) => ({
                    ...prevState,
                    optimize_math_formula: value === 'true',
                  }));
                }}
                disabled={disableOptimizeMathFormula}
                className="inline-grid grid-cols-2 rounded-xl bg-background/70 p-1 shadow-sm"
              >
                <label
                  htmlFor="formula-off"
                  className="flex h-8 cursor-pointer items-center justify-center gap-2 rounded-lg px-4 text-sm text-muted-foreground transition-colors has-[[data-state=checked]]:bg-primary has-[[data-state=checked]]:text-primary-foreground"
                >
                  <RadioGroupItem
                    value="false"
                    id="formula-off"
                    className="sr-only"
                  />
                  不开启
                </label>
                <label
                  htmlFor="formula-on"
                  className="flex h-8 cursor-pointer items-center justify-center gap-2 rounded-lg px-4 text-sm text-muted-foreground transition-colors has-[[data-state=checked]]:bg-primary has-[[data-state=checked]]:text-primary-foreground"
                >
                  <RadioGroupItem
                    value="true"
                    id="formula-on"
                    className="sr-only"
                  />
                  开启
                </label>
              </RadioGroup>
            </section>
          )}

          <section className="rounded-2xl bg-linear-to-br from-primary/14 via-primary/8 to-transparent px-5 py-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="text-xs font-medium text-muted-foreground">
                  本次预计消耗
                </div>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-3xl font-semibold tracking-tight tabular-nums text-primary">
                    {requiredPoint.toLocaleString()}
                  </span>
                  <span className="text-sm text-muted-foreground">积分</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-right text-xs sm:min-w-64">
                <div>
                  <div className="text-muted-foreground">Token</div>
                  <div className="mt-1 font-medium tabular-nums">
                    {fileInfo.source_n_tokens.toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">保存</div>
                  <div className="mt-1 font-medium tabular-nums">
                    {retentionDays} 天
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">余额</div>
                  <div className="mt-1 font-medium tabular-nums">
                    {pointInfo.available.toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {hasRunningTaskPointRisk && (
            <Alert variant="destructive" className="rounded-xl">
              <AlertTriangleIcon className="h-4 w-4" />
              <AlertDescription>
                您有 {pointInfo.task_count_deep_research}{' '}
                个深度研究任务正在运行， 本次翻译任务需要积分余额不少于{' '}
                {(
                  pointInfo.total_running_task_estimated_point_deep_research +
                  requiredPoint
                ).toLocaleString()}{' '}
                才能开启。
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="px-6 pb-6 pt-1 sm:px-8">
          <Button
            variant="ghost"
            className="rounded-full px-5"
            onClick={() => onOpenChange(false)}
          >
            取消
          </Button>
          {showBuyBtn ? (
            <Button className="rounded-full px-5" asChild>
              <Link href="/pricing/credits">获取积分</Link>
            </Button>
          ) : (
            <Button
              className="min-w-28 rounded-full px-5"
              disabled={isLoading || isPending}
              onClick={() => onStartTranslate()}
            >
              {(isLoading || isPending) && (
                <Loader2Icon className="h-4 w-4 animate-spin" />
              )}
              开始翻译
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TranslationFileOptionsModal;
